/**
 * Mapping sécurisé des formulaires Coran Soigne → Order API.
 * Les champs sans colonne dédiée partent dans `informations_complementaires`.
 * Aucune donnée médicale ne doit être loggée côté client.
 */

import type { OrderFormData } from "../../../../../utils/api/order/order.type";

/** Sous-ensemble des champs formulaire service (évite import circulaire). */
export type ServiceFormLike = {
  user?: string | number;
  vendeur?: string | number;
  nom_complet?: string;
  telephone?: string;
  email?: string;
  adresse?: string;
  age?: string;
  profession?: string;
  situation_matrimoniale?: string;
  sexe?: string;
  nationalite?: string;
  religion?: string;
  date_rendez_vous?: string;
  heure_rendez_vous?: string;
  autres_traitements?: string;
  motifs_consultation?: string[];
  type_consultation?: string;
  symptomes?: string[];
  motif_rendez_vous?: string;
  consentement?: boolean;
  produits?: any[];
  montant_total?: number;
};

const MAX_FIELD = 500;
const MAX_TEXTAREA = 2000;
const MAX_COMPLEMENT = 8000;
const MAX_LIST_ITEMS = 25;

/** Retire HTML / caractères de contrôle et tronque. */
export function sanitizeText(
  value: unknown,
  maxLen: number = MAX_FIELD
): string {
  if (value === null || value === undefined) return "";
  let s = String(value)
    .replace(/<[^>]*>/g, "")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (s.length > maxLen) {
    s = s.slice(0, maxLen);
  }
  return s;
}

function sanitizeList(values: unknown, maxItemLen = 120): string[] {
  if (!Array.isArray(values)) return [];
  return values
    .slice(0, MAX_LIST_ITEMS)
    .map((v) => sanitizeText(v, maxItemLen))
    .filter(Boolean);
}

/** Découpe "Prénom Nom" → champs Order dédiés. */
export function splitNomComplet(nomComplet?: string): {
  prenom_client: string;
  nom_client: string;
} {
  const full = sanitizeText(nomComplet, 255);
  if (!full) {
    return { prenom_client: "", nom_client: "" };
  }
  const parts = full.split(" ").filter(Boolean);
  if (parts.length === 1) {
    return { prenom_client: parts[0], nom_client: parts[0] };
  }
  return {
    prenom_client: parts[0],
    nom_client: parts.slice(1).join(" "),
  };
}

/**
 * Sérialise les infos perso + médicales sans champ Order dédié.
 * Préfixe confidentiel pour le vendeur.
 */
export function buildServiceInformationsComplementaires(
  data: ServiceFormLike
): string {
  const lines: string[] = [
    "[CONFIDENTIEL — Données de consultation]",
    "Usage réservé au traitement du rendez-vous. Ne pas diffuser.",
    `Consentement explicite : ${data.consentement === true ? "Oui" : "Non"}`,
    `Horodatage consentement (client) : ${new Date().toISOString()}`,
    "",
    "--- Profil ---",
  ];

  const profile: Array<[string, string]> = [
    ["Âge", sanitizeText(data.age, 20)],
    ["Profession", sanitizeText(data.profession)],
    ["Situation matrimoniale", sanitizeText(data.situation_matrimoniale)],
    ["Sexe", sanitizeText(data.sexe, 40)],
    ["Nationalité", sanitizeText(data.nationalite)],
    ["Religion", sanitizeText(data.religion)],
  ];
  for (const [label, val] of profile) {
    if (val) lines.push(`${label} : ${val}`);
  }

  lines.push("", "--- Rendez-vous ---");
  const rdv: Array<[string, string]> = [
    ["Date", sanitizeText(data.date_rendez_vous, 40)],
    ["Heure", sanitizeText(data.heure_rendez_vous, 40)],
    ["Type de consultation", sanitizeText(data.type_consultation)],
  ];
  for (const [label, val] of rdv) {
    if (val) lines.push(`${label} : ${val}`);
  }

  lines.push("", "--- Informations médicales ---");
  const motifs = sanitizeList(data.motifs_consultation);
  if (motifs.length) {
    lines.push(`Motifs de consultation : ${motifs.join(", ")}`);
  }
  const symptomes = sanitizeList(data.symptomes);
  if (symptomes.length) {
    lines.push(`Symptômes : ${symptomes.join(", ")}`);
  }
  const traitements = sanitizeText(data.autres_traitements, MAX_TEXTAREA);
  if (traitements) {
    lines.push(`Autres traitements : ${traitements}`);
  }
  const motifRdv = sanitizeText(data.motif_rendez_vous, MAX_TEXTAREA);
  if (motifRdv) {
    lines.push(`Motif du rendez-vous : ${motifRdv}`);
  }

  let text = lines.join("\n").trim();
  if (text.length > MAX_COMPLEMENT) {
    text = `${text.slice(0, MAX_COMPLEMENT - 20)}\n…[tronqué]`;
  }
  return text;
}

export type ServiceOrderMeta = {
  env: string;
  page?: string;
  canale_acquisition?: string;
  mode_de_paiement?: string;
  code_commande?: string;
};

/**
 * Construit le payload Order autorisé uniquement (pas de champs médicaux bruts).
 */
export function buildOrderPayloadFromService(
  data: ServiceFormLike,
  meta: ServiceOrderMeta
): OrderFormData {
  if (data.consentement !== true) {
    throw new Error("CONSENT_REQUIRED");
  }

  const { prenom_client, nom_client } = splitNomComplet(data.nom_complet);
  const telephone = sanitizeText(data.telephone, 40);
  const email_client = sanitizeText(data.email, 255);
  const adresse = sanitizeText(data.adresse, 255);

  const payload: OrderFormData = {
    user: data.user,
    vendeur: data.vendeur,
    produits: data.produits,
    montant_total: data.montant_total,
    telephone,
    email_client,
    adresse,
    nom_client: nom_client || sanitizeText(data.nom_complet, 255),
    prenom_client,
    mode_de_paiement: meta.mode_de_paiement || "CASH",
    code_commande: meta.code_commande,
    env: meta.env,
    page: meta.page || "rendez-vous",
    canale_acquisition: meta.canale_acquisition || "Site web",
    informations_complementaires:
      buildServiceInformationsComplementaires(data),
  };

  return payload;
}
